import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  HelpOutlineIcon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  Switch,
  TextField,
  ToolTip,
  Typography
} from '@esgian/esgianui';
import CloseIcon from '@mui/icons-material/Close';
import { useQueryClient } from '@tanstack/react-query';
import { scheduleReport, updateReport } from '@api/ReportScheduler';
import Checkbox from '@mui/material/Checkbox';
import { useAuth } from 'react-oidc-context';
import { useTimezone } from '@hooks/useTimezone';
import { RigSelect } from '@components/Inputs';
import DateRangeDropdown from '@components/DateRangeDropdown';
import { getOffset } from '@api/helpers';
import tz_lookup from 'tz-lookup';
import moment from 'moment-timezone';
import { DATE_FORMAT, TOOLTIP_DATE_READABLE } from '@constants/dates';
import { isTrendAvailable as checkTrendAvailable } from '@helpers/reportTrend';

// Matches ReportFrequency enum
const FREQUENCY_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'BiWeekly', label: 'Bi-Weekly' },
  { value: 'Monthly', label: 'Monthly' }
];

// Matches ReportPeriod enum
const PERIOD_OPTIONS = [
  { value: 'Last24Hours', label: 'Last 24 Hours' },
  { value: 'Last7Days', label: 'Last 7 Days' },
  { value: 'Last14Days', label: 'Last 14 Days' },
  { value: 'LastMonth', label: 'Last Month' },
  { value: 'CurrentWell', label: 'Current Well' },
  { value: 'Custom', label: 'Custom' }
];

const REPORT_TYPE_OPTIONS = [
  { value: 'RigOverview', label: 'Energy overview' },
  { value: 'CounterPeriodSummary', label: 'Maintenance - Equipment counter report' }
];

// Matches DayOfWeek enum
const DAY_OF_WEEK_OPTIONS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Tuesday', label: 'Tuesday' },
  { value: 'Wednesday', label: 'Wednesday' },
  { value: 'Thursday', label: 'Thursday' },
  { value: 'Friday', label: 'Friday' },
  { value: 'Saturday', label: 'Saturday' },
  { value: 'Sunday', label: 'Sunday' }
];

function CreateReportModal({ open, handleClose, lookupRigs, editMode, initialData, onSendNow, onSaved }) {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUserEmail =
    auth.user?.profile?.email || auth.user?.profile?.preferred_username || '';
  const { selectedTimeZone, calculateDates } = useTimezone();

  // Title
  const [reportName, setReportName] = useState(initialData?.reportName || '');

  // Content
  const [selectedRig, setSelectedRig] = useState(() => {
    if (initialData?.rigId && lookupRigs?.length) {
      return lookupRigs.find((r) => r.rigId === initialData.rigId) || null;
    }
    return null;
  });
  const [period, setPeriod] = useState(initialData?.period || 'Last7Days');
  const [periodStartDate, setPeriodStartDate] = useState(
    initialData?.periodStartDate ? moment.tz(initialData.periodStartDate, selectedTimeZone).format(DATE_FORMAT) : ''
  );
  const [periodEndDate, setPeriodEndDate] = useState(
    initialData?.periodEndDate ? moment.tz(initialData.periodEndDate, selectedTimeZone).format(DATE_FORMAT) : ''
  );
  const [reportType, setReportType] = useState(initialData?.reportType || '');

  // Send Details
  const [emails, setEmails] = useState(
    initialData?.emails 
      ? (Array.isArray(initialData.emails) 
        ? initialData.emails.join(', ') 
        : initialData.emails) 
      : currentUserEmail
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(
    initialData?.isImmediate ? false : (editMode ? !!initialData?.frequency : !initialData || !!initialData?.frequency)
  );
  const [frequency, setFrequency] = useState(initialData?.frequency || 'Daily');
  const [deliveryDayOfWeek, setDeliveryDayOfWeek] = useState(initialData?.deliveryDayOfWeek || 'Monday');
  const [startDate, setStartDate] = useState(
    initialData?.startDate ? moment(initialData.startDate).format(DATE_FORMAT) : ''
  );
  const [endDate, setEndDate] = useState(
    initialData?.endDate ? moment(initialData.endDate).format(DATE_FORMAT) : ''
  );

  // Download now (only when not scheduled)
  const [downloadNow, setDownloadNow] = useState(
    initialData?.downloadImmediate ?? !!initialData?.isImmediate
  );

  // Options
  const [showTrend, setShowTrend] = useState(initialData?.options?.showTrend ?? true);
  const [visibility, setVisibility] = useState(initialData?.visibility?.toLowerCase() || 'company');

  const [submitting, setSubmitting] = useState(false);

  const isFormValid = () => {
    return reportName.trim() && selectedRig && emails.trim() && reportType;
  };

  // Show Trend is not applicable to the counter report
  const isCounterReport = reportType === 'CounterPeriodSummary';

  // A rig only supports the report types configured for it in report_info, delivered with the rig lookup.
  const supportedReportTypes = useMemo(
    () => selectedRig?.supportedReportTypes ?? [],
    [selectedRig]
  );

  const defaultReportType = useMemo(
    () =>
      REPORT_TYPE_OPTIONS.find(({ value }) => supportedReportTypes.includes(value))?.value || '',
    [supportedReportTypes]
  );

  // An existing selection stays listed even when unsupported, so the Select never holds an out-of-range value.
  const reportTypeOptions = useMemo(() => {
    if (!reportType || REPORT_TYPE_OPTIONS.some(({ value }) => value === reportType)) {
      return REPORT_TYPE_OPTIONS;
    }
    return [...REPORT_TYPE_OPTIONS, { value: reportType, label: reportType }];
  }, [reportType]);

  useEffect(() => {
    if (editMode) return;
    if (!selectedRig) {
      setReportType('');
      return;
    }
    if (reportType && supportedReportTypes.includes(reportType)) return;
    setReportType(defaultReportType);
  }, [editMode, selectedRig, supportedReportTypes, reportType, defaultReportType]);

  const isTrendAvailable = useMemo(
    () =>
      checkTrendAvailable({
        period,
        periodStartDate,
        periodEndDate,
        lowerLimit: selectedRig?.lowerLimit
      }),
    [period, periodStartDate, periodEndDate, selectedRig]
  );

  useEffect(() => {
    if (!isTrendAvailable) setShowTrend(false);
  }, [isTrendAvailable]);

  const getRigTimezone = () => {
    if (selectedRig?.latitude != null && selectedRig?.longitude != null) {
      return tz_lookup(selectedRig.latitude, selectedRig.longitude);
    }
    return selectedTimeZone;
  };

  const getRigOffset = () => {
    return getOffset(moment().tz(getRigTimezone()).format());
  };

  const getPeriodDates = () => {
    const reportTimezone = getRigTimezone();
    const { dateStart, dateEnd } = calculateDates({startDate: periodStartDate, endDate: periodEndDate, timezone: reportTimezone});

    return { dateStart, dateEnd };
  };

  const getScheduledRequestDates = () => {
    const requestStartDate = startDate
        ? moment.tz(startDate, selectedTimeZone).startOf('day').toISOString()
        : moment().toISOString();
    const requestEndDate = endDate
        ? moment.tz(endDate, selectedTimeZone).endOf('day').toISOString()
        : moment().add(1, 'months').toISOString();

    return { requestStartDate, requestEndDate };
  }

  const buildBaseBody = () => {
    const emailList = emails
      .replace(/;/g, ',')
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);

    const rigOffset = getRigOffset();

    const { requestStartDate, requestEndDate } = getScheduledRequestDates();

    const body = {
      emails: emailList,
      frequency: scheduleEnabled ? frequency : 'Daily',
      period,
      deliveryDayOfWeek: scheduleEnabled && frequency !== 'Daily' && frequency !== 'Monthly' ? deliveryDayOfWeek : undefined,
      startDate: requestStartDate,
      endDate: requestEndDate,
      reportName: reportName || null,
      visibility,
      options: {
        // Show Trend does not apply to the counter report
        ...(isCounterReport ? {} : { showTrend }),
        offset: rigOffset
      }
    };

    if (period === 'Custom' && periodStartDate && periodEndDate) {
      const { dateStart, dateEnd } = getPeriodDates();
      body.periodStartDate = dateStart;
      body.periodEndDate = dateEnd;
    }

    return body;
  };

  const buildRequestBody = (sendImmediately = false) => ({
    ...buildBaseBody(),
    reportType,
    rigId: selectedRig?.rigId,
    rigName: selectedRig?.rigName || '',
    sendImmediately,
    downloadImmediate: !scheduleEnabled && downloadNow ? true : undefined
  });

  const buildUpdateBody = ({ sendImmediately = false } = {}) => {
    const body = buildBaseBody();
    if (sendImmediately) {
      body.sendImmediately = true;
    }
    return body;
  };
  
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const scheduleReportControllerRef = useRef(null);
  const handleSaveControllerRef = useRef(null);
  useEffect(() => {
    return () => {
      scheduleReportControllerRef.current?.abort();
      handleSaveControllerRef.current?.abort();
    };
  }, []);


  const handleSave = async () => {
    if (!isFormValid()) return;
    setSubmitting(true);
    try {
      if (editMode && initialData?.id) {
        handleSaveControllerRef.current = new AbortController();
        await updateReport(handleSaveControllerRef.current.signal, initialData.id, buildUpdateBody());
      } else {
        handleSaveControllerRef.current = new AbortController();
        await scheduleReport(handleSaveControllerRef.current.signal, buildRequestBody(false));
        onSaved?.();
      }
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'archived'] });
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (!isFormValid()) return;
    if (editMode && initialData?.id) {
      setSubmitting(true);
      try {
        handleSaveControllerRef.current = new AbortController();
        await updateReport(handleSaveControllerRef.current.signal, initialData.id, buildUpdateBody({ sendImmediately: true }));
        queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'executions'] });
        queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'history'] });
        handleClose();
      } finally {
        setSubmitting(false);
      }
    } else {
      const body = buildRequestBody(true);
      handleClose();
      if (onSendNow) {
        onSendNow(body);
      }
    }
  };

  const handleSaveAndSendScheduled = async () => {
    if (!isFormValid()) return;
    setSubmitting(true);
    try {
      if (editMode && initialData?.id) {
        handleSaveControllerRef.current = new AbortController();
        await updateReport(handleSaveControllerRef.current.signal, initialData.id, buildUpdateBody({ sendImmediately: true }));
      } else {
        scheduleReportControllerRef.current = new AbortController();
        await scheduleReport(scheduleReportControllerRef.current.signal, buildRequestBody(true));
        onSaved?.();
      }
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'myRequests'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'executions'] });
      queryClient.invalidateQueries({ queryKey: ['scheduledReport', 'history'] });
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const setPreset = ({ period, frequency, deliveryDayOfWeek }) => {
    const reportTimezone = getRigTimezone();

    setPeriod(period);
    setFrequency(frequency);
    setDeliveryDayOfWeek(deliveryDayOfWeek);
    setScheduleEnabled(true);
    setStartDate(moment().tz(reportTimezone).format(DATE_FORMAT));
    setEndDate(moment().tz(reportTimezone).add(1, 'year').format(DATE_FORMAT));
  }

  return (
    <Modal
      closeIcon={<CloseIcon fontSize="small" />}
      PaperProps={{
        sx: {
          background: ({ palette }) => palette.neutral.neutral01
        }
      }}
      size="sm"
      open={open}
      handleClose={handleClose}
      DialogActionsProps={{ sx: { justifyContent: 'flex-end', px: 3, gap: 1 } }}
      actions={[
        <Button
          key="cancel-button"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.secondary.contrastText }}
          color="secondary"
          variant="text">
          Cancel
        </Button>,
        scheduleEnabled && (
          <Button
            key="save-button"
            disabled={!isFormValid() || submitting}
            onClick={handleSave}
            variant="contained"
            sx={{ borderRadius: '100px' }}>
            Save
          </Button>
        ),
        <Button
          key="save-send-button"
          disabled={!isFormValid() || submitting}
          onClick={scheduleEnabled ? handleSaveAndSendScheduled : handleSaveAndSend}
          variant="contained"
          sx={{ borderRadius: '100px' }}>
          Save and Send Out
        </Button>
      ].filter(Boolean)}
      title={editMode ? 'Edit report' : 'Create a report'}>
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Preset Buttons */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Apply template:
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPreset({ 
                  period: 'Last24Hours',
                  frequency: 'Daily',
                  deliveryDayOfWeek: 'Monday'
                });
              }}>
              Daily
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPreset({ 
                  period: 'Last7Days',
                  frequency: 'Weekly',
                  deliveryDayOfWeek: 'Monday'
                });
              }}>
              Weekly
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPreset({ 
                  period: 'LastMonth',
                  frequency: 'Monthly',
                  deliveryDayOfWeek: 'Monday'
                });
              }}>
              Monthly
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setPreset({ 
                  period: 'CurrentWell',
                  frequency: 'Weekly',
                  deliveryDayOfWeek: 'Sunday'
                });
              }}>
              Current Well
            </Button>
          </Stack>

          {/* Title Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Report title
            </Typography>
            <TextField
              fullWidth
              required
              label="Title"
              placeholder="Give a title for the report"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              size="small"
            />
          </Box>

          {/* Content Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Content
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <RigSelect
                  selectedRig={selectedRig}
                  disabled={editMode}
                  onChange={(rig) => setSelectedRig(rig)}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Report Period</InputLabel>
                  <Select
                    label="Report Period"
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value);
                      if (e.target.value === 'Custom') {
                        setScheduleEnabled(false);
                      }
                    }}>
                    {PERIOD_OPTIONS.map((opt) => {
                      let disabled = false;
                      if (opt.value === 'LastMonth') {
                        disabled = scheduleEnabled && frequency !== 'Monthly';
                      }
                      return (
                        <MenuItem key={opt.value} value={opt.value} disabled={disabled}>
                          {opt.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              {period === 'Custom' && (
                <Grid item xs={12}>
                  <DateRangeDropdown
                    onSave={(item) => {
                      setPeriodStartDate(item.startDate || '');
                      setPeriodEndDate(item.endDate || '');
                    }}
                    startDate={periodStartDate}
                    endDate={periodEndDate}
                    fullWidth
                    allowTimeZoneChange={false}
                    currentRig={selectedRig}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Module</InputLabel>
                  <Select
                    label="Module"
                    value={reportType}
                    disabled={editMode || !selectedRig}
                    onChange={(e) => setReportType(e.target.value)}>
                    {reportTypeOptions.map((type) => (
                      <MenuItem
                        key={type.value}
                        value={type.value}
                        disabled={!supportedReportTypes.includes(type.value)}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {!selectedRig
                      ? 'Select a rig to see the reports it supports'
                      : !supportedReportTypes.length
                      ? 'No reports are configured for this rig'
                      : ' '}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            {!isCounterReport && (
              <Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <FormControlLabel
                    disabled={!isTrendAvailable}
                    control={
                      <Switch
                        checked={isTrendAvailable && showTrend}
                        disabled={!isTrendAvailable}
                        onChange={(e) => {
                          if (!isTrendAvailable) return;
                          setShowTrend(e.target.checked);
                        }}
                      />
                    }
                    label="Show trend"
                    sx={{ mr: 0 }}
                  />
                  <ToolTip title="It will not display trend if there is not enough data.">
                    <HelpOutlineIcon sx={{ fontSize: '16px' }} />
                  </ToolTip>
                </Stack>
                {!isTrendAvailable && (
                  <FormHelperText>
                    The rig has no data for the comparison period preceding the selected range
                  </FormHelperText>
                )}
              </Stack>
            )}
          </Box>

          {/* Send Details Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Send Details: Recipients and Schedule
            </Typography>

            {/* Recipients */}
            <TextField
              fullWidth
              required
              label="Recipients"
              placeholder="Add recipients' email"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              size="small"
              multiline
              minRows={1}
              sx={{ mb: 2 }}
            />

            {/* Schedule toggle */}
            {period !== 'Custom' && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Schedule details
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={scheduleEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setScheduleEnabled(enabled);
                        if (enabled && period === 'LastMonth' && frequency !== 'Monthly') {
                          setFrequency('Monthly');
                        }
                      }}
                    />
                  }
                  label={scheduleEnabled ? 'Scheduled' : 'No schedule'}
                  labelPlacement="start"
                />
              </Stack>
            )}

            {/* Download Now option (only when not scheduled) */}
            {!scheduleEnabled && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={downloadNow}
                    onChange={(e) => setDownloadNow(e.target.checked)}
                  />
                }
                label="Download Now"
                sx={{ mb: 2 }}
              />
            )}

            {/* Schedule fields */}
            {scheduleEnabled && (
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Send frequency</InputLabel>
                    <Select
                      label="Send frequency"
                      value={frequency}
                      onChange={(e) => {
                        const newFreq = e.target.value;
                        setFrequency(newFreq);
                        if (period === 'LastMonth' && newFreq !== 'Monthly') {
                          setPeriod('Last7Days');
                        }
                      }}>
                      {FREQUENCY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4} sx={{ position: 'relative' }}>
                  <input
                    type="date"
                    ref={startDateRef}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  />
                  <TextField
                    fullWidth
                    label="Schedule start"
                    value={startDate ? moment.tz(startDate, selectedTimeZone).format(TOOLTIP_DATE_READABLE) : ''}
                    size="small"
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: !!startDate }}
                    onClick={() => {
                      const el = startDateRef.current;
                      if (el?.showPicker) { el.showPicker(); } else { el?.focus(); el?.click(); }
                    }}
                    sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
                  />
                </Grid>
                <Grid item xs={4} sx={{ position: 'relative' }}>
                  <input
                    type="date"
                    ref={endDateRef}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                  />
                  <TextField
                    fullWidth
                    label="Schedule end"
                    value={endDate ? moment.tz(endDate, selectedTimeZone).format(TOOLTIP_DATE_READABLE) : ''}
                    size="small"
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: !!endDate }}
                    onClick={() => {
                      const el = endDateRef.current;
                      if (el?.showPicker) { el.showPicker(); } else { el?.focus(); el?.click(); }
                    }}
                    sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
                  />
                </Grid>
                {frequency !== 'Daily' && frequency !== 'Monthly' && (
                  <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Delivery day</InputLabel>
                      <Select
                        label="Delivery day"
                        value={deliveryDayOfWeek}
                        onChange={(e) => setDeliveryDayOfWeek(e.target.value)}>
                        {DAY_OF_WEEK_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>

          {/* Options */}
          <Box>
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>Visibility</InputLabel>
              <Select
                label="Visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}>
                <MenuItem value="user">Only Me</MenuItem>
                <MenuItem value="company">Company</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}

CreateReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lookupRigs: PropTypes.array,
  editMode: PropTypes.bool,
  initialData: PropTypes.object,
  onSendNow: PropTypes.func,
  onSaved: PropTypes.func
};

CreateReportModal.defaultProps = {
  lookupRigs: [],
  editMode: false,
  initialData: null
};

export default CreateReportModal;
